/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description getPaymentTableDetails
 */
import _ from 'lodash';
import dateParser from 'lib/dateParser';
import { YEAR_MONTH_DATE_FORMAT, YEAR_MONTH_DATE_TIME_FORMAT_WITH_TIMEZONE } from '@veritable/common/constants';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { PAYMENT_METHODS } from 'app-constants/payments';
import { PAYMENT_RECONCILIATION_OUTCOME, PAYMENT_RECONCILIATION_STATUS } from 'app-constants/claims/era';
import { getClaimInfo } from 'utils/payment/paymentAllocation';
import { getName, getIdentifierValue } from 'utils/parseFhirDataType';
import { getOutstandingPR, getParsedGuarantor } from 'utils/accounts/guarantor';
import { fromCents, toCents } from 'utils/claims/postPayment';
import { getClaimResponseFinancialMeta } from 'utils/claims/claimInformation';
import { CLAIM_TYPES } from 'app-constants/claims/claimRequest';

const getPaymentAttachments = ({
	attachments,
}: {
	attachments?: { fileName?: string; fileType?: string; blobId?: string }[];
}): PatientPayment.IParsedPaymentReconciliation['files'] => {
	return _.map(attachments, ({ fileName, fileType, blobId }) => ({
		url: blobId || '',
		name: fileName || '',
		type: fileType || '',
	}));
};

const getPaymentSubmitAttachments = (
	files?: PatientPayment.ICloudflareUploadFile[],
): PatientPayment.IPaymentReconciliationModel['attachments'] => {
	return _.map(files, (file) => ({
		blobId: file.blobId || file.url || file.uid || '',
		fileName: file.name || '',
		fileType: file.type || '',
	}));
};

/**
 * Get payment methods from payment reconciliation
 */
const getPaymentMethod = (data: Fhir.PaymentReconciliation): string => {
	const paymentMethodCode = _.find(data?.method?.coding, (payment) => payment?.system === fhirSystemMapping.PaymentMethod)?.code;
	const methodDetails = _.find(PAYMENT_METHODS, (method) => method?.value === paymentMethodCode);
	return methodDetails?.label || paymentMethodCode || '';
};

/**
 * Get parsed payment reconciliation
 */
const getParsedPaymentReconciliation = (
	fhirPayment: Fhir.PaymentReconciliation,
	fhirGuarantor: Fhir.Patient | Fhir.RelatedPerson | Fhir.Organization,
	attachments?: { fileName?: string; fileType?: string; blobId?: string }[],
): PatientPayment.IParsedPaymentReconciliation => {
	const parsedData = {} as PatientPayment.IParsedPaymentReconciliation;
	parsedData.id = fhirPayment?.id;
	parsedData.fhirResource = fhirPayment;
	parsedData.paymentAmount = fhirPayment?.amount?.value || 0;
	parsedData.creationDate = fhirPayment?.created;
	parsedData.collectedDate = fhirPayment?.date;
	parsedData.collectedAmount = fhirPayment?.amount?.value || 0;
	parsedData.status = fhirPayment?.status;
	parsedData.outcome = fhirPayment?.outcome;
	parsedData.paymentMethod = _.find(fhirPayment?.method?.coding, (payment) => payment?.system === fhirSystemMapping.PaymentMethod)?.code;
	parsedData.referenceId = fhirPayment?.referenceNumber;
	parsedData.description = fhirPayment?.processNote?.[0]?.text;
	parsedData.refundAmount = fhirPayment?.returnedAmount?.value || 0;
	parsedData.files = getPaymentAttachments({ attachments });
	parsedData.refundReason = _.find(
		fhirPayment?.returnedAmount?.extension,
		(extension) => extension?.url === fhirSystemMapping.refundReason,
	)?.valueString;

	const paymentIssuerReference = fhirPayment?.paymentIssuer?.reference;

	const parsedGuarantor = getParsedGuarantor(fhirGuarantor);
	const guarantorReference = `${fhirGuarantor?.resourceType}/${fhirGuarantor?.id}`;
	if (guarantorReference === paymentIssuerReference) {
		parsedData.paymentIssuer = {
			reference: guarantorReference,
			name: parsedGuarantor?.name,
			id: fhirGuarantor?.id,
			key: fhirGuarantor?.id,
		};
	}

	const allocations = _.map(fhirPayment?.allocation, (allocation: Fhir.PaymentReconciliationAllocation) => {
		return {
			id: allocation?.id,
			amount: allocation?.amount?.value || 0,
			claimResponseId: _.split(allocation?.response?.reference, '/')[1],
			claimId: _.split(allocation?.target?.reference, '/')[1],
		};
	});

	const allocatedCents = _.sumBy(allocations, ({ amount }) => toCents(amount));
	parsedData.allocatedAmount = fromCents(allocatedCents);
	parsedData.allocations = allocations;
	parsedData.unallocatedAmount = parsedData.paymentAmount - parsedData.allocatedAmount - parsedData.refundAmount;
	return parsedData;
};

/**
 * Get payment table details
 */
const getPaymentTableDetails = (data: PatientPayment.IAllPayments): PatientPayment.IParsedPaymentReconciliation[] => {
	return _.map(data?.data, ({ attachments, fhirResource, fhirGuarantor, eraNumber }) => {
		const paymentFhirResource = fhirResource as Fhir.PaymentReconciliation;
		const parsedPayment = getParsedPaymentReconciliation(paymentFhirResource, fhirGuarantor as Fhir.RelatedPerson, attachments);
		return {
			...parsedPayment,
			eraNumber,
		};
	});
};

/**
 * Build payment reconciliation resource
 */
const getPaymentReconciliationResource = ({
	formValues,
	paymentFhirResource,
}: {
	formValues: PatientPayment.IRecordPaymentFormModel;
	paymentFhirResource?: Fhir.PaymentReconciliation;
}): Fhir.PaymentReconciliation => {
	const fhirResource = paymentFhirResource ? _.cloneDeep(paymentFhirResource) : ({} as Fhir.PaymentReconciliation);
	const currentModifierExtensions = _.filter(
		fhirResource?.modifierExtension || [],
		(extension) => extension?.url !== fhirSystemMapping.userAttachment,
	);
	const referenceId = _.trim(formValues?.referenceId || '');

	fhirResource.date = dateParser(formValues?.date).format(YEAR_MONTH_DATE_FORMAT);
	fhirResource.amount = {
		...fhirResource?.amount,
		value: _.toNumber(formValues?.amount) || 0,
		currency: fhirResource?.amount?.currency || 'USD',
	};
	fhirResource.paymentIssuer = {
		...fhirResource?.paymentIssuer,
		reference: formValues?.selectedGuarantor?.reference || '',
	};
	if (_.isEmpty(currentModifierExtensions)) {
		delete fhirResource.modifierExtension;
	} else {
		fhirResource.modifierExtension = currentModifierExtensions;
	}
	if (referenceId) {
		fhirResource.referenceNumber = formValues?.referenceId;
	} else {
		delete fhirResource.referenceNumber;
	}
	fhirResource.method = {
		coding: [
			{
				system: fhirSystemMapping.PaymentMethod,
				code: formValues?.paymentMethod,
				display: PAYMENT_METHODS[formValues?.paymentMethod]?.label,
			},
		],
	};
	fhirResource.processNote = [
		{
			type: 'display',
			text: formValues?.description,
		},
	];
	return fhirResource;
};

/**
 * Get record payment submit model
 */
const getRecordPaymentSubmitModel = ({
	formValues,
}: {
	formValues: PatientPayment.IRecordPaymentFormModel;
}): PatientPayment.IPaymentReconciliationModel => {
	const fhirResource = getPaymentReconciliationResource({ formValues });
	const attachments = getPaymentSubmitAttachments(formValues?.files);
	fhirResource.type = {
		text: 'payment',
	};
	fhirResource.outcome = PAYMENT_RECONCILIATION_OUTCOME.queued;
	fhirResource.status = PAYMENT_RECONCILIATION_STATUS.active;
	fhirResource.created = dateParser().format(YEAR_MONTH_DATE_TIME_FORMAT_WITH_TIMEZONE);
	return {
		fhirResource,
		allocations: [],
		attachments,
	};
};

/**
 * Get update payment submit model
 */
const getUpdatePaymentSubmitModel = ({
	formValues,
	paymentFhirResource,
}: {
	formValues: PatientPayment.IRecordPaymentFormModel;
	paymentFhirResource: Fhir.PaymentReconciliation;
}): PatientPayment.IPaymentReconciliationPatchModal => {
	const fhirResource = getPaymentReconciliationResource({ formValues, paymentFhirResource });
	return {
		fhirResource,
		allocations: [],
	};
};

const getPaymentSummaryTableData = ({
	allAllocations,
	parsedAllocations,
}: {
	allAllocations?: PatientPayment.IPaymentReconciliationAllocation[];
	parsedAllocations?: PatientPayment.IParsedPaymentReconciliation['allocations'];
}): PatientPayment.ISummaryTableData[] => {
	if (!parsedAllocations?.length) {
		return [];
	}

	return _.map(parsedAllocations, ({ amount, claimId }, index) => {
		const allocation = _.find(allAllocations, ({ fhirClaim: allocationFhirClaim }) => allocationFhirClaim?.id === claimId);
		const { collectedPR, fhirClaim, fhirClaimResponse, fhirPatient, accountInfo, writeOffMetadata } = allocation || {};
		const claimTypeCode = fhirClaim?.type?.coding?.[0]?.code;
		const isInstitutionalClaim = claimTypeCode === CLAIM_TYPES.institutional.value;
		const claimInfo = getClaimInfo(fhirClaim as Fhir.Claim, isInstitutionalClaim, {});

		const { patientResponsibilityAmount } = getClaimResponseFinancialMeta(fhirClaimResponse as Fhir.ClaimResponse);
		const writeOffAmount = _.sumBy(writeOffMetadata, (metaData) => metaData?.amount);
		const pendingPRAmount = _.isNumber(patientResponsibilityAmount)
			? patientResponsibilityAmount - (collectedPR || 0) - (writeOffAmount || 0)
			: 0;
		const pendingPR = getOutstandingPR({
			patientResponsibility: patientResponsibilityAmount,
			allocated: collectedPR,
			writeOffAmount,
		});

		return {
			key: index,
			patientIdentifier: {
				name: getName(fhirPatient),
				id: fhirPatient?.id,
			},
			claimIdentifier: {
				name: claimInfo.patientControlNumber || claimInfo.claimNumber,
				id: fhirClaim?.id,
			},
			accountIdentifier: {
				accountNumber: getIdentifierValue(accountInfo?.identifier, fhirSystemMapping.accountNumber),
				id: accountInfo?.accountId,
				accountAge: accountInfo?.accountAge,
			},
			allocatedAmount: amount ?? 0,
			pendingPR,
			pendingPRAmount,
			writeOffAmount,
		};
	});
};

const blockNonNumericInput = (event: React.KeyboardEvent<HTMLInputElement>): void => {
	const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter', 'Home', 'End', '.'];
	if (event.ctrlKey || event.metaKey || _.includes(allowedKeys, event.key) || /^\d$/.test(event.key)) {
		return;
	}
	event.preventDefault();
};

export {
	blockNonNumericInput,
	getPaymentTableDetails,
	getPaymentSummaryTableData,
	getRecordPaymentSubmitModel,
	getUpdatePaymentSubmitModel,
	getPaymentMethod,
	getParsedPaymentReconciliation,
};
